/** Hooks for the additive /v2 per-entity endpoints - one targeted conditional
 * read/write per character/location/shot/META, instead of the whole-project
 * GET/POST. Entirely new surface: does not import from or modify api.ts, and the
 * existing whole-project hooks (useProjectDetails/useUpdateProject/...) and the
 * existing (dead) per-entity hooks in api.ts are untouched. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import {
  type DeleteEntityV2Vars,
  deleteEntityV2WithRetry,
  fetchEntityV2,
  fetchProjectMetaV2Request,
  type ReorderEntityV2Vars,
  reorderEntityV2WithRetry,
  type SaveEntityV2Vars,
  saveEntityV2WithRetry,
  updateProjectMetaV2WithRetry,
  type V2EntityKind
} from "@/services/entityOpsV2";

export { EntityConflictError, type V2EntityItem, type V2EntityKind, type V2MetaItem, type V2ReorderResult } from "@/services/entityOpsV2";

/** GET a single character/location/shot with its version - the baseline the next
 * useUpdate*V2/useReorderEntityV2/useDeleteEntityV2 call needs as `expectedVersion`. */
export const useEntityV2 = <TData>(projectId: string | null, kind: V2EntityKind | null, entityId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["entityV2", projectId, kind, entityId],
    queryFn: async () => fetchEntityV2<TData>(axiosClient, projectId!, kind!, entityId!),
    enabled: Boolean(projectId) && Boolean(kind) && Boolean(entityId),
    retry: 1,
    refetchOnWindowFocus: false
  });
};

function useSaveEntityV2<TData>(kind: V2EntityKind) {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: SaveEntityV2Vars<TData>) {
      return saveEntityV2WithRetry<TData>(axiosClient, kind, vars);
    }
  });
}

export const useUpdateCharacterV2 = <TData = Record<string, unknown>>() => useSaveEntityV2<TData>("character");
export const useUpdateLocationV2 = <TData = Record<string, unknown>>() => useSaveEntityV2<TData>("location");
export const useUpdateShotV2 = <TData = Record<string, unknown>>() => useSaveEntityV2<TData>("shot");

export const useDeleteEntityV2 = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: DeleteEntityV2Vars) {
      await deleteEntityV2WithRetry(axiosClient, vars);
    }
  });
};

export const useReorderEntityV2 = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: ReorderEntityV2Vars) {
      return reorderEntityV2WithRetry(axiosClient, vars);
    }
  });
};

/** GET just the project-level META fields with their version. */
export const useProjectMetaV2 = <TData>(projectId: string | null) => {
  const axiosClient = useAxios();
  const enabled = Boolean(projectId);
  return useQuery({
    queryKey: ["projectMetaV2", projectId],
    queryFn: async () => fetchProjectMetaV2Request<TData>(axiosClient, projectId!),
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateProjectMetaV2 = <TData>() => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly data: Partial<TData>; readonly expectedVersion: number }) {
      return updateProjectMetaV2WithRetry<TData>(axiosClient, vars.projectId, vars.data, vars.expectedVersion);
    },
    onSuccess(data, vars) {
      queryClient.setQueryData(["projectMetaV2", vars.projectId], data);
    }
  });
};
